import { createAudioContext, ensureAudioContextRunning } from "./audioContext";
import { AudioTransport } from "./transport";
import { createTsAudioKernel } from "../kernel/tsKernel";
import type { AudioKernel } from "../kernel/abi";
import type { KernelMessage } from "../types";
import {
  applyTelemetryMessage,
  createEmptyTelemetry,
  type EngineTelemetry,
  type KernelTelemetryMessage,
} from "../telemetry";
import { CURVA_AUDIO_PROCESSOR_NAME, createWorkletModuleUrl } from "../worklet/workletModuleUrl";

export type AudioEngineMode = "worklet" | "direct";

export type CreateAudioEngineOptions = {
  /**
   * `worklet` — realtime via AudioWorklet (browser).
   * `direct` — call kernel on the host thread (tests / offline render; no speakers unless you wire output).
   */
  mode?: AudioEngineMode;
  /** Override worklet module URL (bundled asset). Default: blob from `createWorkletModuleUrl`. */
  workletUrl?: string;
  sampleRate?: number;
};

export type PortOutboundHandler = (data: unknown) => void;

export type AudioEngine = {
  readonly mode: AudioEngineMode;
  readonly context: AudioContext | undefined;
  /**
   * Worklet output node (stereo) connected to the destination — tap this for
   * bus capture. Undefined in direct mode.
   */
  readonly master: AudioNode | undefined;
  readonly transport: AudioTransport;
  /** Direct-mode kernel (undefined in worklet mode). */
  readonly kernel: AudioKernel | undefined;
  /** Shared clock for transport + PatternPlayer (AudioContext or direct fake time). */
  getCurrentTime(): number;
  /** Advance direct-mode clock (no-op in worklet mode). */
  advanceTime(seconds: number): void;
  /** Post a kernel (or worklet music) message to the audio thread / direct kernel. */
  post(message: KernelMessage | Record<string, unknown>, transfer?: Transferable[]): void;
  /** Observe worklet→main messages (music playhead / notes). No-op in direct mode. */
  addPortListener?(handler: PortOutboundHandler): void;
  removePortListener?(handler: PortOutboundHandler): void;
  /** Latest track + master levels (updated ~every 256 samples). */
  getTelemetry(): EngineTelemetry;
  resume(): Promise<void>;
  /** Render `seconds` of audio in direct mode (interleaved stereo LRLR). */
  renderOffline(seconds: number): Float32Array;
  dispose(): void;
};

/**
 * Create the Curva audio engine.
 *
 * Worklet mode: AudioContext + AudioWorkletNode → DSP kernel.
 * Direct mode: in-process `TsAudioKernel` for unit tests and offline renders.
 */
export async function createAudioEngine(options: CreateAudioEngineOptions = {}): Promise<AudioEngine> {
  const mode = options.mode ?? (typeof AudioWorkletNode !== "undefined" ? "worklet" : "direct");

  if (mode === "direct") {
    return createDirectEngine(options.sampleRate ?? 48000);
  }

  return createWorkletEngine(options);
}

function createDirectEngine(sampleRate: number): AudioEngine {
  const kernel = createTsAudioKernel();
  kernel.init({ sampleRate });
  kernel.handleMessage({ type: "init", sampleRate });

  let fakeTime = 0;
  const post = (message: KernelMessage, _transfer?: Transferable[]) => {
    kernel.handleMessage(message);
  };
  const transport = new AudioTransport(post, () => fakeTime);

  return {
    mode: "direct",
    context: undefined,
    master: undefined,
    transport,
    kernel,
    getCurrentTime: () => fakeTime,
    advanceTime(seconds: number) {
      fakeTime += Math.max(0, seconds);
    },
    post,
    getTelemetry: () => kernel.getTelemetry(),
    async resume() {},
    renderOffline(seconds: number) {
      const frames = Math.max(1, Math.floor(seconds * sampleRate));
      const left = new Float32Array(frames);
      const right = new Float32Array(frames);
      const start = fakeTime;
      kernel.process(left, right, frames, start);
      fakeTime = start + seconds;
      const interleaved = new Float32Array(frames * 2);
      for (let i = 0; i < frames; i++) {
        interleaved[i * 2] = left[i]!;
        interleaved[i * 2 + 1] = right[i]!;
      }
      return interleaved;
    },
    dispose() {
      transport.stop();
      kernel.handleMessage({ type: "allNotesOff" });
    },
  };
}

async function createWorkletEngine(options: CreateAudioEngineOptions): Promise<AudioEngine> {
  const context = createAudioContext(
    options.sampleRate ? { sampleRate: options.sampleRate } : undefined,
  );
  const workletUrl = options.workletUrl ?? createWorkletModuleUrl();
  const ownsBlobUrl = !options.workletUrl;

  await context.audioWorklet.addModule(workletUrl);
  const node = new AudioWorkletNode(context, CURVA_AUDIO_PROCESSOR_NAME, {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2],
  });
  node.connect(context.destination);

  const telemetry = createEmptyTelemetry();
  const portListeners = new Set<PortOutboundHandler>();
  node.port.onmessage = (event: MessageEvent<KernelTelemetryMessage | { type: string }>) => {
    const data = event.data;
    if (data && data.type === "telemetry") {
      applyTelemetryMessage(telemetry, data as KernelTelemetryMessage);
    }
    for (const handler of portListeners) {
      try {
        handler(data);
      } catch (error) {
        console.warn("[audio-engine] port listener error", error);
      }
    }
  };

  const post = (message: KernelMessage | Record<string, unknown>, transfer?: Transferable[]) => {
    if (transfer && transfer.length > 0) {
      node.port.postMessage(message, transfer);
    } else {
      node.port.postMessage(message);
    }
  };
  post({ type: "init", sampleRate: context.sampleRate });

  const transport = new AudioTransport(
    (message, transfer) => post(message, transfer),
    () => context.currentTime,
  );

  return {
    mode: "worklet",
    context,
    master: node,
    transport,
    kernel: undefined,
    getCurrentTime: () => context.currentTime,
    advanceTime() {},
    post,
    addPortListener(handler) {
      portListeners.add(handler);
    },
    removePortListener(handler) {
      portListeners.delete(handler);
    },
    getTelemetry: () => telemetry,
    async resume() {
      await ensureAudioContextRunning(context);
    },
    renderOffline() {
      throw new Error("renderOffline is only available in direct mode");
    },
    dispose() {
      transport.stop();
      portListeners.clear();
      node.port.onmessage = null;
      node.disconnect();
      void context.close();
      if (ownsBlobUrl) {
        URL.revokeObjectURL(workletUrl);
      }
    },
  };
}
